import { Link } from 'react-router-dom';
import { Chip } from './Chip';
import { WorkspaceCleanupStatus } from './WorkspaceCleanupStatus';
import { useProjectScopeOptional } from '@/context/ProjectContext';
import { formatTimestamp, runRequirement } from '@/utils/format';
import type { PipelineRunListItemDto } from '@/types/api';

export function RunCard({ run }: { run: PipelineRunListItemDto }): JSX.Element {
  const isLive = run.state === 'running' || run.state === 'paused_for_approval';
  const signals = run.signals;
  const scope = useProjectScopeOptional();
  const base = scope ? `/p/${scope.project.slug}` : '';

  return (
    <Link
      to={`${base}/runs/${encodeURIComponent(run.id)}`}
      className="run"
      data-testid={`run-card-${run.id}`}
      data-live={isLive ? 'true' : 'false'}
    >
      <div className="run-top">
        <div className="run-name truncate" title={runRequirement(run.inputs)}>
          {runRequirement(run.inputs)}
        </div>
        <Chip state={run.state} />
      </div>
      <div className="run-sub text-[11px] text-text-muted">{run.workflow_name}</div>
      <div className="run-id">{run.id}</div>
      <div className="run-meta">
        <span>
          latest stage · <span className="text-text-dim">{run.latest_stage ?? '—'}</span>
        </span>
        <span>
          started · <span className="text-text-dim">{formatTimestamp(run.started_at)}</span>
        </span>
        <WorkspaceCleanupStatus run={run} />
        {signals ? <SignalBadges signals={signals} runId={run.id} /> : null}
      </div>
      {isLive ? (
        <span
          aria-hidden="true"
          className="absolute right-4 top-3.5 w-[7px] h-[7px] rounded-full bg-ok animate-pulse"
        />
      ) : null}
    </Link>
  );
}

function SignalBadges({
  signals,
  runId
}: {
  signals: NonNullable<PipelineRunListItemDto['signals']>;
  runId: string;
}): JSX.Element {
  void runId;
  return (
    <span className="inline-flex gap-1.5 ml-auto flex-wrap">
      {signals.stalled ? (
        <span
          className="inline-flex items-center gap-1 text-[11px] px-2 py-px rounded-[10px] font-medium bg-warn/15 text-warn"
          data-testid="badge-stalled"
        >
          <span className="w-1 h-1 rounded-full bg-warn" />
          stalled
        </span>
      ) : null}
    </span>
  );
}
